import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/site';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: 'https://91wang.com/',
    },
    url: `https://91wang.com/article/${article.slug}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://91wang.com/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://91wang.com/', name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': `https://91wang.com/category/${article.category}`, name: category?.name || article.category } },
      { '@type': 'ListItem', position: 3, item: { '@id': `https://91wang.com/article/${article.slug}`, name: article.title } },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - ${siteInfo.name}`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},91网`}
        canonicalUrl={`https://91wang.com/article/${article.slug}`}
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />
      <Navbar />
      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || article.category, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mt-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  {category.name}
                </Link>
              )}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                {article.readTime} 分钟阅读
              </span>
            </div>
          </header>

          <div className="w-full rounded-lg overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - ${siteInfo.name}`}
              className="w-full h-auto object-cover"
            />
          </div>

          <div
            className="prose prose-lg max-w-none prose-headings:text-foreground-900 prose-p:text-foreground-700 prose-p:leading-relaxed prose-strong:text-foreground-900 prose-a:text-primary-500 prose-a:no-underline hover:prose-a:text-primary-600 prose-li:text-foreground-700 prose-h2:text-xl prose-h2:font-bold prose-h2:mt-10 prose-h2:mb-4"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 mr-2">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h3 className="text-xl font-bold text-foreground-900 mb-6">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-full h-36 overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-400 mt-2">{ra.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}